/**
 * Outlook frontend-tool handlers.
 *
 * Each entry is dispatched by the backend via a LangGraph `interrupt()` —
 * the SSE stream emits a `tool_call` event carrying a `handler` key that
 * matches a property name in `handlers`. The function runs Office.js
 * APIs, returns a JSON-serialisable result, and the wrapper in
 * `run-handler.ts` POSTs it back to `/api/v1/chat/resume`.
 *
 * Adding a new Outlook tool = adding one entry here + one Cosmos document
 * under `integrations/frontend_tools/outlook.json` with a matching
 * `frontend_binding.handler` string.
 */

import type { EmailSearchResult } from "../../../src/integrations/email/types";
import type { Pill } from "../../../src/chat/types";
import { emitPills } from "./pill-bridge";
import { emitEmailCards } from "./email-cards-bridge";
import { emitToast } from "./toast-bridge";
import { requestConfirm } from "./confirm-bridge";
import { getGraphAccess } from "./graph-bridge";
import { openExistingAppointment, toEwsId } from "./appointment";
import { createCalendarEvent } from "../../../src/integrations/email/services/graph";
import { execCommandCopyFallback, htmlToClipboardText } from "../clipboard";
import { escapeHtml } from "./html";
import { REPLY_TARGET_CONVERSATION_ARG } from "./replyTarget";
import { toRestId } from "../../../src/integrations/email/hooks/useMailboxItem";

// ---------------------------------------------------------------------------
// Tenant disclaimer — ST-09, closing gap G-14
// ---------------------------------------------------------------------------
//
// ST-09: *"Where enabled, a tenant disclaimer is appended idempotently to
// outbound bodies — appending twice must not produce two copies."*
//
// This was a no-op, on the reasoning that the backend already embeds the
// disclaimer in `draft["body"]` (email_draft_submit_tool.py) before the pill
// args reach us. That is true only when a DRAFT EXISTS. The add-in builds the
// action bar itself now (docs/outlook-action-pills-design.md), and on a
// text-producing turn — summarise, action items, a general answer — there is no
// draft, so the button carries `textToEmailHtml(answerText)` straight from the
// streamed reply. That body never passed through the backend's appender, which
// is exactly what G-14 predicted: *"the compose fast path may bypass it
// entirely. Apply the disclaimer on both write paths."*
//
// DR-01's two write paths both converge here, so this is the one choke point
// that covers both:
//   - compose fast path  → `fillComposeBody`  (setSelectedDataAsync / setAsync)
//   - Graph / native path → `composeReply` + `openCompose`
//
// Deliberately NOT applied to `clipboard.copy`: ST-08 wants *"the raw response
// text, not the draft-sanitised variant"* on the clipboard.
//
// The settings come from Studio, not from here — `GET /addin/settings/{channel}`
// returns `outlookDisclaimer: {isEnabled, text}` (AddinSettingsManager reads the
// tenant's OutlookIntegrationSettings row), and TaskPane pushes it in below.
// Disabled, unset, or a blank text all mean "append nothing".
interface OutlookDisclaimerSettings {
  enabled: boolean;
  text: string;
}

let _disclaimerSettings: OutlookDisclaimerSettings = { enabled: false, text: "" };

export function setOutlookDisclaimerSettings(settings: { enabled: boolean; text: string }): void {
  _disclaimerSettings = { enabled: !!settings.enabled, text: settings.text ?? "" };
}

// Byte-identical to `_DISCLAIMER_HR` / `_DISCLAIMER_P` in
// integrations/systemic_tools/submit_tools/email_draft_submit_tool.py. It has to
// be: a draft body already labelled by the backend must be recognised here as
// already carrying the disclaimer, or a Graph-path draft opened through
// `compose.reply` would get a second copy — the exact failure ST-09's AC names.
const DISCLAIMER_HR = '<hr style="border:none;border-top:1px solid #cccccc;margin:8px 0">';
const DISCLAIMER_P = '<p style="color:#888888;font-size:11px;font-style:italic">';

/**
 * Append the tenant disclaimer to an outbound body, at most once.
 *
 * Idempotency is checked against BOTH the raw and the HTML-escaped text, which
 * is what makes it hold across the two layers as well as across repeat clicks:
 * the backend appends the raw value, this appends the escaped one, and either
 * form found in the body means the work is already done. So the specification's
 * case — *"drafting twice from the same response still yields one copy"* —
 * holds whichever path produced the first copy.
 *
 * An empty body is left alone: a Fill compose click that inserted nothing but a
 * disclaimer would be a confusing write, not a labelled one.
 */
export function withOutlookDisclaimer(body: string, format: string): string {
  const text = _disclaimerSettings.enabled ? _disclaimerSettings.text.trim() : "";
  if (!text || !body) return body;
  const escaped = escapeHtml(text);
  if (body.includes(text) || body.includes(escaped)) return body;
  return format === "html"
    ? `${body}<br>${DISCLAIMER_HR}${DISCLAIMER_P}${escaped}</p>`
    : `${body}\n\n---\n${text}`;
}

export type HandlerArgs = Record<string, unknown>;
export type HandlerResult = Record<string, unknown>;
export type HandlerFn = (args: HandlerArgs) => Promise<HandlerResult>;

function asyncCallback<T>(
  fn: (cb: (r: Office.AsyncResult<T>) => void) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    fn((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message ?? "Office.js call failed"));
      }
    });
  });
}

async function openCompose(args: HandlerArgs): Promise<HandlerResult> {
  const to = (args.to as string[] | undefined) ?? [];
  const cc = (args.cc as string[] | undefined) ?? [];
  const subject = (args.subject as string | undefined) ?? "";
  const body = (args.body as string | undefined) ?? "";
  const bodyFormat = (args.body_format as string | undefined) ?? "html";
  const draftId = typeof args.draft_id === "string" ? args.draft_id : "";

  const mailbox = Office.context?.mailbox;

  // Preferred path: the backend pre-created a Graph draft (with attachments already
  // embedded). Open it directly via displayMessageForm — attachment appears automatically.
  if (draftId && mailbox?.displayMessageForm) {
    // Graph hands back a base64URL REST id; displayMessageForm wants EWS base64.
    // It has no callback, so a REST id opens nothing and reports nothing — see
    // toEwsId. The calendar path has always converted; this one did not.
    mailbox.displayMessageForm(toEwsId(draftId));
    return { opened: true, source: "graph_draft" };
  }

  if (!mailbox?.displayNewMessageFormAsync) {
    throw new Error("displayNewMessageFormAsync is not available in this host");
  }

  // Fallback: open a fresh compose form without attachments. The body is going
  // into a real outbound message, so it carries the tenant disclaimer (ST-09) —
  // the `draftId` branch above does not, because that draft was already labelled
  // by the backend before it was created.
  return new Promise<HandlerResult>((resolve, reject) => {
    mailbox.displayNewMessageFormAsync(
      {
        toRecipients: to,
        ccRecipients: cc,
        subject,
        htmlBody: bodyFormat === "html" ? withOutlookDisclaimer(body, "html") : undefined,
      },
      (res: Office.AsyncResult<void>) => {
        if (res.status === Office.AsyncResultStatus.Succeeded) {
          resolve({ opened: true, source: "native_form" });
        } else {
          reject(new Error(res.error?.message ?? "Failed to open compose form"));
        }
      },
    );
  });
}

async function readCurrentMailbox(): Promise<HandlerResult> {
  const item = Office.context?.mailbox?.item;
  if (!item) {
    throw new Error("No mail item is currently open");
  }

  const subject =
    typeof item.subject === "string" ? item.subject : "";
  const fromAddress =
    item.from && typeof item.from === "object" && "emailAddress" in item.from
      ? (item.from as Office.EmailAddressDetails).emailAddress ?? ""
      : "";
  const to = (item.to ?? []).map((r) => r.emailAddress);

  const body = await asyncCallback<string>((cb) =>
    item.body.getAsync(Office.CoercionType.Text, cb),
  );

  return {
    subject,
    from: fromAddress,
    to,
    body,
    item_id: item.itemId ?? "",
  };
}


async function readComposeSelection(item: Office.MessageCompose): Promise<string> {
  const getSelected = item.getSelectedDataAsync as unknown as
    | ((
        coercionType: Office.CoercionType,
        callback: (result: Office.AsyncResult<{ data?: unknown }>) => void,
      ) => void)
    | undefined;
  if (typeof getSelected !== "function") return "";
  try {
    const value = await asyncCallback<{ data?: unknown }>((cb) =>
      getSelected.call(item, Office.CoercionType.Text, cb),
    );
    return typeof value?.data === "string" ? value.data : "";
  } catch {
    return "";
  }
}

/**
 * Insert the drafted body into the open compose window, at the cursor.
 *
 * DR-01 specifies exactly this: *"`setSelectedDataAsync(…, Html)` inserts the body
 * at the cursor (fallback `setAsync`), preserving quoted history below"*, repeated
 * in its AC-2. The caret — rather than replacing the whole body — is what keeps the
 * quoted thread intact in a reply-compose window.
 *
 * ## Gap G-3 — consent before replacing a selection (now closed)
 *
 * `setSelectedDataAsync` replaces a non-empty SELECTION, silently, with no undo in
 * the pane. DR-01 requires user-typed content is never destroyed without consent,
 * so we read the selection first and, when it holds real content, confirm before
 * replacing it. The confirmation goes through the in-pane `confirm-bridge` dialog
 * — NOT `window.confirm`/`alert`/`prompt`, which the Outlook webview blocks
 * outright (*"Function window.confirm is not supported"*). The bridge round-trips
 * the decision through React state and back before this handler continues.
 *
 * Every failure mode fails closed: a declined confirm, an unmounted dialog, or a
 * `runHandler` timeout while the dialog is open all leave the selection intact.
 */
async function fillComposeBody(rawText: string, format: string): Promise<HandlerResult> {
  const coercionType =
    format === "html" ? Office.CoercionType.Html : Office.CoercionType.Text;

  // The compose fast path is the one G-14 names as bypassing ST-09 outright.
  // It writes into a message the user is about to send, so it is an outbound
  // body like any other and must carry the disclaimer.
  const text = withOutlookDisclaimer(rawText, format);

  const composeItem = Office.context?.mailbox?.item as Office.MessageCompose | undefined;
  if (!composeItem?.body) {
    throw new Error("No active compose pane");
  }


  const selection = await readComposeSelection(composeItem);
  if (selection.trim().length > 0) {
    const confirmed = await requestConfirm({
      title: "Replace selected text?",
      message:
        "The text you've selected in the message will be replaced. This can't be undone here.",
      confirmLabel: "Replace",
      cancelLabel: "Cancel",
    });
    if (!confirmed) {
      return { inserted: false, cancelled: true };
    }
  }

  // DR-01 specifies `setSelectedDataAsync` with `setAsync` as the fallback. The
  // fallback matters on hosts that expose a settable body but not a settable
  // SELECTION — without it the whole compose fast path throws, and DR-01's fast
  // path is the one drafting route that works with no external service at all
  // (ST-03), so it must not be the first thing to break.
  //
  // `setAsync` replaces the entire body, which loses quoted history — so it is
  // strictly a last resort, never preferred.
  if (typeof composeItem.body.setSelectedDataAsync === "function") {
    await asyncCallback<void>((cb) =>
      composeItem.body.setSelectedDataAsync(text, { coercionType }, cb),
    );
    return { inserted: true, source: "compose_fast" };
  }
  if (typeof composeItem.body.setAsync === "function") {
    await asyncCallback<void>((cb) =>
      composeItem.body.setAsync(text, { coercionType }, cb),
    );
    return { inserted: true, replacedBody: true, source: "compose_fast" };
  }
  throw new Error("No active compose pane");
}

/**
 * `compose.insert` — DR-01's two write paths behind the *Fill compose* button.
 *
 * The specification gives DR-01 one entry point with the fast path *"taken first
 * whenever a compose window is open"* and, in the same breath, *"on failure it
 * falls through to the Graph path"*. Only the first half was implemented: the
 * insert threw `No active compose pane` and stopped there, so the moment the
 * probe came back empty — a pill clicked after the compose window was closed, a
 * host with no settable body, a stale pill clicked from the reading pane — the
 * one drafting route that works with no external service at all (ST-03) had no
 * route at all. That is what this restores.
 *
 * Success toasts *"Response added to compose window"* per DR-01. A DECLINED G-3
 * confirm is returned as-is and never falls through: declining must write
 * nothing, so it cannot become a reason to open a window instead.
 *
 * The Graph args ride along on the pill (`fill_compose` in
 * chat/pills/outlookPills.ts) purely so this fallback has a draft to open. The
 * fast path itself never reads them — §5 is explicit that it leaves subject and
 * recipients alone, because Outlook has already set them on the open window.
 */
async function insertAtCursor(args: HandlerArgs): Promise<HandlerResult> {
  const text = (args.text as string | undefined) ?? "";
  const format = (args.format as string | undefined) ?? "html";
  try {
    const result = await fillComposeBody(text, format);
    if (result.inserted) {
      emitToast({ title: "Response added to compose window", intent: "success" });
    }
    return result;
  } catch {
    return openCompose({ ...args, body: text, body_format: format });
  }
}

// ---------------------------------------------------------------------------
// Pill emission handlers
//
// Every action pill is a distinct frontend tool in ToolRegistry. The LLM calls
// each pill tool with the args it wants baked in; the corresponding handler
// here constructs the final `Pill` record (resolving any LLM-supplied fields
// into the label / message / args shape the UI expects) and emits it via the
// `emitPills` bridge. One handler call → one pill button rendered.
//
// Naming: `ui.pill.<category>.<action>` mirrors the JSON id `pill_<category>_<action>`.
// ---------------------------------------------------------------------------

// Inbox refinement pill — one parametric tool for all canonical refinements.
// The LLM supplies `kind` from a closed enum; the handler maps it to the
// final label + message. Adding a new refinement = one entry here + one enum
// value in the tool's input_schema in ToolRegistry. No new tool, no new
// handler. LLM drift is impossible because `kind` is enum-validated by the
// LLM's tool-call schema before this handler runs.
const REFINEMENT_KINDS: Record<string, { label: string; message: string }> = {
  // email_assistant is the catch-up skill (unread inbox, last N days, enforced
  // in the backend), so this refinement narrows THAT set — the message says
  // "unread ... with attachments" to match what actually comes back, not
  // "recent emails". The window is env-driven, so no hard "7 days" here.
  with_attachments: {
    label: "With attachments",
    message: "Show my unread emails with attachments from the last few days.",
  },
  show_unread: {
    label: "Show unread",
    message: "Show me my unread emails.",
  },
  last_7_days: {
    label: "Last 7 days",
    message: "Show me my unread emails from the last few days.",
  },
};

async function pillChatRefineInbox(args: HandlerArgs): Promise<HandlerResult> {
  const kind = typeof args.kind === "string" ? args.kind : "";
  const spec = REFINEMENT_KINDS[kind];
  if (!spec) {
    throw new Error(
      `pill_chat_refine_inbox: unknown kind "${kind}". Known kinds: ${Object.keys(REFINEMENT_KINDS).join(", ")}`,
    );
  }
  emitPills([{ type: "chat_intent", label: spec.label, message: spec.message }]);
  return { rendered: true };
}

async function pillOfficeComposeOpen(args: HandlerArgs): Promise<HandlerResult> {
  const to = (args.to as string[] | undefined) ?? [];
  const cc = (args.cc as string[] | undefined) ?? [];
  const subject = typeof args.subject === "string" ? args.subject : "";
  const body = typeof args.body === "string" ? args.body : "";
  const bodyFormat =
    args.body_format === "text" || args.body_format === "html" ? args.body_format : "html";
  // When the backend pre-created a Graph draft (e.g. via create_compose_draft),
  // the draft_id is passed here so openCompose can use displayMessageForm instead
  // of displayNewMessageFormAsync — the draft already contains the attachment.
  const draftId = typeof args.draft_id === "string" ? args.draft_id : "";
  // Empty `to` is permitted (backend validator only rejects INVALID addresses,
  // not absent ones; Outlook's compose form lets the user add recipients
  // before sending). Only block when subject or body are missing — those
  // break the prefill entirely.
  if (!subject || !body) {
    throw new Error("pill_office_compose_open requires subject and body");
  }
  emitPills([
    {
      type: "office_action",
      label: "Open",
      handler: "compose.open",
      args: { to, cc, subject, body, body_format: bodyFormat, ...(draftId ? { draft_id: draftId } : {}) },
    },
  ]);
  return { rendered: true };
}

async function pillOfficeClipboardCopy(args: HandlerArgs): Promise<HandlerResult> {
  // Convert before the empty check, not after: "<p></p>" is non-empty raw but
  // copies nothing, and it would render a Copy button that throws on click.
  // The backend emitters skip the pill in that case; this keeps the model-driven
  // path consistent with them.
  const text = htmlToClipboardText(typeof args.text === "string" ? args.text : "");
  if (!text) {
    throw new Error("pill_office_clipboard_copy requires non-empty text");
  }
  emitPills([
    {
      type: "office_action",
      label: "Copy",
      handler: "clipboard.copy",
      args: { text },
    },
  ]);
  return { rendered: true };
}

async function pillOfficeComposeReply(args: HandlerArgs): Promise<HandlerResult> {
  const body = typeof args.body === "string" ? args.body : "";
  const mode =
    args.mode === "reply" || args.mode === "reply_all" ? args.mode : "reply";
  const subject = typeof args.subject === "string" ? args.subject : "";
  const to = (args.to as string[] | undefined) ?? [];
  const cc = (args.cc as string[] | undefined) ?? [];
  const draftId = typeof args.draft_id === "string" ? args.draft_id : undefined;
  if (!body) {
    throw new Error("pill_office_compose_reply requires non-empty body");
  }
  emitPills([
    {
      type: "office_action",
      label: "Reply",
      handler: "compose.reply",
      args: {
        body,
        mode,
        subject,
        to,
        cc,
        ...(draftId ? { draft_id: draftId } : {}),
      },
    },
  ]);
  return { rendered: true };
}

// Tone-cycle pill — one parametric tool for all 4 tone variants. Same pattern
// as pill_chat_refine_inbox: LLM supplies `kind` from a closed enum; handler
// maps to the canonical label + chat message. LLM cannot drift on label or
// message because `kind` is enum-validated by the tool-call schema before this
// handler runs. The chat message is self-contained (no "this draft" deixis) so
// it survives long chat histories.
const TONE_KINDS: Record<string, { label: string; message: string }> = {
  formal: {
    label: "Make formal",
    message: "Make the previous draft more formal — keep the same recipients and intent.",
  },
  casual: {
    label: "Make casual",
    message: "Make the previous draft more casual and friendly — keep the same recipients and intent.",
  },
  shorter: {
    label: "Make shorter",
    message: "Make the previous draft more concise — keep the same recipients and intent.",
  },
  empathetic: {
    label: "More empathetic",
    message: "Make the previous draft more empathetic — keep the same recipients and intent.",
  },
};

async function pillChatToneAdjustment(args: HandlerArgs): Promise<HandlerResult> {
  const kind = typeof args.kind === "string" ? args.kind : "";
  const spec = TONE_KINDS[kind];
  if (!spec) {
    throw new Error(
      `pill_chat_tone_adjustment: unknown kind "${kind}". Known kinds: ${Object.keys(TONE_KINDS).join(", ")}`,
    );
  }
  emitPills([{ type: "chat_intent", label: spec.label, message: spec.message }]);
  return { rendered: true };
}

// Search refinement pill — self-routing chat_intent for email_search.
// Same closed-enum pattern as pill_chat_refine_inbox, but messages are
// search-flavoured so the router classifies clicks back to email_search
// rather than email_assistant.
const SEARCH_REFINEMENT_KINDS: Record<string, { label: string; message: string }> = {
  with_attachments: {
    label: "With attachments",
    message: "Search my mailbox for emails with attachments.",
  },
  without_attachments: {
    label: "Without attachments",
    message: "Search my mailbox for emails without attachments.",
  },
  show_unread: {
    label: "Show unread",
    message: "Search my mailbox for unread emails.",
  },
  just_today: {
    label: "Just today",
    message: "Search my mailbox for emails from today.",
  },
  last_7_days: {
    label: "Last 7 days",
    message: "Search my mailbox for emails from the last 7 days.",
  },
  last_30_days: {
    label: "Last 30 days",
    message: "Search my mailbox for emails from the last 30 days.",
  },
};

async function pillChatRefineSearch(args: HandlerArgs): Promise<HandlerResult> {
  const kind = typeof args.kind === "string" ? args.kind : "";
  const spec = SEARCH_REFINEMENT_KINDS[kind];
  if (!spec) {
    throw new Error(
      `pill_chat_refine_search: unknown kind "${kind}". Known kinds: ${Object.keys(SEARCH_REFINEMENT_KINDS).join(", ")}`,
    );
  }
  emitPills([{ type: "chat_intent", label: spec.label, message: spec.message }]);
  return { rendered: true };
}

// Calendar pills — three tools cover all calendar-skill emission cases:
// (1) pill_office_calendar_open  — office_action when a draft is ready
// (2) pill_chat_book_slot        — parametric chat_intent for slot picks
// (3) pill_chat_meeting_followup — free-form chat_intent for situational followups

async function pillOfficeCalendarOpen(args: HandlerArgs): Promise<HandlerResult> {
  const subject = typeof args.subject === "string" ? args.subject : "";
  const body = typeof args.body === "string" ? args.body : "";
  const start = typeof args.start === "string" ? args.start : "";
  const end = typeof args.end === "string" ? args.end : "";
  const attendees = (args.attendees as string[] | undefined) ?? [];
  const kind = args.kind === "teams" ? "teams" : "regular";
  // For Teams meetings the body has already been composed server-side
  // (joinInformation banner embedded). Default location is fixed by the
  // calendar.open_teams handler — we don't pass one through.
  const suppliedLocation = typeof args.location === "string" ? args.location : "";
  const location = kind === "teams" ? "" : suppliedLocation;

  // Empty attendees is fine — a meeting with no attendees is a personal
  // calendar block (the backend draft also allows this). Only block when the
  // truly required scalar fields (subject/start/end) are missing.
  if (!subject || !start || !end) {
    throw new Error("pill_office_calendar_open requires subject, start, and end");
  }

  const pillLabel = kind === "teams" ? "Open Teams meeting" : "Open";
  const pillHandler = kind === "teams" ? "calendar.open_teams" : "calendar.open";

  emitPills([
    {
      type: "office_action",
      label: pillLabel,
      handler: pillHandler,
      args: { subject, body, start, end, attendees, location },
    },
  ]);
  return { rendered: true };
}

// Format the slot pill label from an ISO start datetime. Returns strings like
// "Book Tue 11am", "Book Wed 2:30pm" — short weekday + 12-hour time with
// lowercase meridiem and no leading zero. Used by pill_chat_book_slot so the
// LLM does not have to manually format dates (a common source of slip-ups —
// "Book Tue 11" without meridiem is ambiguous between 11am and 11pm).
function _formatClockLabel(startIso: string): string | null {
  const d = new Date(startIso);
  if (Number.isNaN(d.getTime())) return null;
  const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const meridiem = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  const minPart = minutes > 0 ? `:${minutes.toString().padStart(2, "0")}` : "";
  return `${weekday} ${hours}${minPart}${meridiem}`;
}

function _formatSlotLabel(startIso: string): string {
  const clock = _formatClockLabel(startIso);
  return clock ? `Book ${clock}` : "Book slot";
}

// Line 1 of the specification's slot card (§6.2, Figure 6.2): "Tue 29 Jul,
// 10:00 – 10:45" — short weekday, day, short month, then the 24-hour start–end
// range separated by an en dash. Verbatim the shape the figure draws, and the
// reason the card needs no "Book" prefix: the chevron and the picker's own
// "Pick a slot" lead-in already say what the control does.
//
// Falls back to the capsule label when either datetime is unparseable, so a
// malformed descriptor degrades to today's button rather than an empty card.
function _formatSlotCardLabel(startIso: string, endIso: string): string | null {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  // Day-before-month order is assembled explicitly rather than left to
  // toLocaleDateString's date-style formats: those follow the host locale, which
  // renders "Tue, Jun 9" on an en-US host and would not match the figure. The
  // weekday and month NAMES still localise; only the order is pinned.
  const weekday = start.toLocaleDateString(undefined, { weekday: "short" });
  const month = start.toLocaleDateString(undefined, { month: "short" });
  const hhmm = (d: Date) => `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
  return `${weekday} ${start.getDate()} ${month}, ${hhmm(start)} – ${hhmm(end)}`;
}

// A time-offer follow-up whose label names no time is indistinguishable from its
// siblings. The reschedule-conflict path asks for 2-3 alternative times, and the
// model labels them all "Change time" — three buttons that look identical but do
// different things, which is worse than a duplicate (AIVA-6393). When the label
// carries no digit but the message carries an ISO datetime, name the time from
// the message so each button says what it will do.
const ISO_IN_MESSAGE = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?)/;
const MAX_PILL_LABEL_CHARS = 32;

function timeAwareLabel(label: string, message: string): string {
  if (/\d/.test(label)) return label;
  const iso = ISO_IN_MESSAGE.exec(message)?.[1];
  const clock = iso ? _formatClockLabel(iso) : null;
  if (!clock) return label;
  const derived = `Move to ${clock}`;
  return derived.length <= MAX_PILL_LABEL_CHARS ? derived : label;
}

// Format the chat-intent message for a slot pill. Self-contained — embeds the
// concrete date, time, attendees, and subject so the next turn can route back
// to calendar_assistant and submit immediately without re-asking.
function _formatSlotMessage(
  startIso: string,
  endIso: string,
  subject: string,
  attendees: readonly string[],
): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const duration =
    !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())
      ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000))
      : 60;
  const attendeesPart =
    attendees.length > 0 ? ` with ${attendees.join(", ")}` : "";
  // A pill click is by construction a USER-CONFIRMED slot: the user explicitly
  // selected this exact start/end by clicking. The pill-seeded verified_slot
  // record carries user_confirmed=True (pill_click_parser.build_pill_verified_slot),
  // so the message claims time_source='user_confirmed_slot'. The previous
  // 'find_slots' wording nudged the model to RE-RUN the find_slots tool —
  // re-showing the slot list instead of booking (AIVA-5523/5163 booking loop).
  // Naming verified_slot_id explicitly stops the model grabbing a stale range
  // slot_id from earlier find_slots output in chat history.
  const slotKey = `slot ${startIso}`;
  return (
    `Schedule a ${duration}-minute meeting${attendeesPart}, subject "${subject}", ` +
    `starting at ${startIso} (local time). Use time_source='user_confirmed_slot', ` +
    `verified_slot_id='${slotKey}', and time_quote='${slotKey}'. ` +
    // This message enumerates the args to pass, and the model fills precisely
    // those and nothing else — it submitted body="" against a prompt asking for
    // an agenda (AIVA-6393). Naming body here is what actually gets it written;
    // the backend pre-gate refuses an empty one when booking from an email.
    `If an email is open, also set body to a short HTML agenda drawn from that ` +
    `email's latest message (Purpose, 2-4 discussion points, decisions/next steps).`
  );
}

// Attendee availability on a book-slot pill (AIVA-7026). Both args are supplied
// by the backend (find_slots → getSchedule) and are absent when the meeting has
// no attendees or their free/busy could not be read — in which case the pill
// renders exactly as it did before.
//
// `availability_percent` is present ONLY when every attendee's calendar was
// readable. When it is absent but a note exists, the availability is partial and
// the compact suffix says so rather than showing a number: the score's own
// percentage is over the READABLE attendees, so "2 of 3 free, 1 unknown" would
// compute as 100 and render a badge for a calendar we never saw.
//
// This is the minimal, layout-free treatment so the flow can be verified
// end-to-end: a compact suffix on the label plus the full note as a hover title.
// The two-line / slot-card treatment the specification's Figure 6.2 shows is
// tracked as outstanding frontend work in ADR-cm01-attendee-availability §9.
/**
 * Pure formatter for a `pill_chat_book_slot` descriptor's args into the
 * renderable slot-card `Pill` — no Office.js, no emission, so it can also run
 * on thread rehydration (AIVA-7622: `mapPill` in threadsApi.ts calls this
 * directly on the raw descriptor `dto.pills` already persists unchanged).
 * Every input is already known at descriptor-build time
 * (`_build_book_slot_pills` in manage_calendar_tool.py) — nothing here reads
 * live calendar state, so replaying an older descriptor is exactly as safe as
 * replaying it the moment it was built. Returns null when the descriptor is
 * missing a required field, e.g. a malformed/legacy payload.
 */
export function buildBookSlotPill(args: HandlerArgs): Pill | null {
  const startIso = typeof args.start_iso === "string" ? args.start_iso : "";
  const endIso = typeof args.end_iso === "string" ? args.end_iso : "";
  const subject = typeof args.subject === "string" ? args.subject : "";
  const attendees = (args.attendees as string[] | undefined) ?? [];
  if (!startIso || !endIso || !subject) return null;
  // Line 2 is the backend's rendered availability, used verbatim. We do NOT read
  // `availability_percent` here: it is present only when every attendee's
  // calendar was readable, and the note already says the right thing in both
  // cases — deriving a number from a partial read is how a false "100%" ships.
  const sublabel = typeof args.availability_note === "string" ? args.availability_note : "";
  const cardLabel = _formatSlotCardLabel(startIso, endIso);
  return {
    type: "chat_intent",
    // The card carries the time range on line 1 (§6.2). Unparseable datetimes
    // fall back to the capsule label so the pill still works.
    ...(cardLabel ? { variant: "slot" as const, label: cardLabel } : { label: _formatSlotLabel(startIso) }),
    // _formatSlotMessage is deliberately untouched: pill_click_parser
    // regex-parses this message to rebuild verified_slots, so availability
    // must never enter it (ADR §8).
    message: _formatSlotMessage(startIso, endIso, subject, attendees),
    ...(sublabel ? { sublabel } : {}),
    // booksSlot (AIVA-6986) is what makes ActionPills lock the picker after a
    // click — G-9. It is separate from `variant: "slot"` above on purpose:
    // `variant` says how to DRAW the pill, `booksSlot` says what CLICKING it
    // does. A follow-up pill could book without being drawn as a card, and a
    // card could exist that books nothing, so neither implies the other.
    booksSlot: true,
  };
}

async function pillChatBookSlot(args: HandlerArgs): Promise<HandlerResult> {
  const pill = buildBookSlotPill(args);
  if (!pill) {
    throw new Error("pill_chat_book_slot requires start_iso, end_iso, and subject");
  }
  emitPills([pill]);
  return { rendered: true };
}

async function pillChatMeetingFollowup(args: HandlerArgs): Promise<HandlerResult> {
  const label = typeof args.label === "string" ? args.label.trim() : "";
  const message = typeof args.message === "string" ? args.message : "";
  if (!label || !message) {
    throw new Error("pill_chat_meeting_followup requires non-empty label and message");
  }
  if (label.length > MAX_PILL_LABEL_CHARS) {
    throw new Error(
      `pill_chat_meeting_followup label must be <= ${MAX_PILL_LABEL_CHARS} chars (got ${label.length}: ${label})`,
    );
  }
  emitPills([{ type: "chat_intent", label: timeAwareLabel(label, message), message }]);
  return { rendered: true };
}

// general_outlook capability launcher — semantically distinct from meeting_followup
// (strict per-skill coupling) even though the on-wire shape is the same. Message
// is a natural-language phrase tuned to the router's cardinal disambiguation rules
// (e.g. "Catch me up on what needs my attention" → email_assistant — the SAME
// message the /catchup slash sends, so pill and slash return an identical window).
// The general_outlook skill prompt enforces the exact label/message mapping per skill.
async function pillChatCapabilityLaunch(args: HandlerArgs): Promise<HandlerResult> {
  const label = typeof args.label === "string" ? args.label.trim() : "";
  const message = typeof args.message === "string" ? args.message.trim() : "";
  if (!label || !message) {
    throw new Error("pill_chat_capability_launch requires non-empty label and message");
  }
  if (label.length > 32) {
    throw new Error(
      `pill_chat_capability_launch label must be <= 32 chars (got ${label.length}: ${label})`,
    );
  }
  emitPills([{ type: "chat_intent", label, message }]);
  return { rendered: true };
}

// Generic backend-attached chat_intent pill. NOT called by the LLM — the
// backend emits it as a display_pill at end-of-turn (see agent_base
// _build_summary_pills), e.g. the summarise skill's "Summarise" re-run pill.
// The message is generic (no specific-email reference) and routes via the
// router on click. Kept generic so any skill can backend-attach a chat_intent.
async function pillChatIntent(args: HandlerArgs): Promise<HandlerResult> {
  const label = typeof args.label === "string" ? args.label.trim() : "";
  const message = typeof args.message === "string" ? args.message : "";
  if (!label || !message) {
    throw new Error("ui.pill.chat.intent requires non-empty label and message");
  }
  if (label.length > 32) {
    throw new Error(`ui.pill.chat.intent label must be <= 32 chars (got ${label.length}: ${label})`);
  }
  emitPills([{ type: "chat_intent", label, message }]);
  return { rendered: true };
}

// Backend-attached, NOT called by the LLM — find_user_by_name appends a
// __NAME_PILLS__ marker to its own JSON output when the directory search
// returns more than one candidate, and agent_base's _collect_name_candidate_pills
// flushes it as this handler's display_pill, one call per candidate. Renders
// a MULTI-SELECT pill (type: "name_candidate") — clicking toggles the
// candidate in/out of the chat input's name list rather than submitting
// anything; useChat.handlePillClick owns the toggle/splice logic. The
// backend sends only label + text (no separate id), so `id` falls back to
// the text itself — good enough since two candidates sharing an identical
// display name is the one case a bare name could never disambiguate anyway.
/**
 * Attendees the same turn resolved uniquely, as sent on a name-candidate pill.
 *
 * They produced no pill of their own — a name matching exactly one person has
 * nothing to disambiguate — so the backend attaches the same settled list to every
 * candidate, letting whichever one the user picks rebuild the FULL attendee set
 * instead of narrowing the request to the single ambiguous name.
 *
 * Entries arrive over the wire, so each is validated rather than trusted: anything
 * without a usable name is dropped, and a malformed payload yields an empty list
 * rather than throwing — a broken hint must not cost the user their pills.
 */
function parseAlreadyResolved(value: unknown): { name: string; mail?: string }[] {
  if (!Array.isArray(value)) return [];
  const parsed: { name: string; mail?: string }[] = [];
  for (const entry of value) {
    const name = (entry as { name?: unknown })?.name;
    if (typeof name !== "string" || !name.trim()) continue;
    // Must BE a string, not merely coercible to one: `String({})` yields
    // "[object Object]", which would then be carried as a recipient address.
    const mail = (entry as { mail?: unknown })?.mail;
    const address = typeof mail === "string" ? mail.trim() : "";
    parsed.push({ name: name.trim(), ...(address ? { mail: address } : {}) });
  }
  return parsed;
}

async function pillChatNameCandidate(args: HandlerArgs): Promise<HandlerResult> {
  const label = typeof args.label === "string" ? args.label.trim() : "";
  const text = typeof args.text === "string" ? args.text.trim() : "";
  if (!label || !text) {
    throw new Error("ui.pill.chat.prefill requires non-empty label and text");
  }
  if (label.length > MAX_PILL_LABEL_CHARS) {
    throw new Error(
      `ui.pill.chat.prefill label must be <= ${MAX_PILL_LABEL_CHARS} chars (got ${label.length}: ${label})`,
    );
  }
  // Carried as hidden metadata onto the eventual turn (see useChat's
  // buildResolvedContactsSuffix) — never shown in the chat bubble itself.
  const mail = typeof args.mail === "string" ? args.mail.trim() : "";
  const alreadyResolved = parseAlreadyResolved(args.already_resolved);
  emitPills([
    {
      type: "name_candidate",
      id: text,
      label,
      name: text,
      ...(mail ? { mail } : {}),
      ...(alreadyResolved.length > 0 ? { alreadyResolved } : {}),
    },
  ]);
  return { rendered: true };
}

// Some Outlook hosts (notably classic desktop Outlook with the Edge WebView2
// runtime) load the add-in iframe without `clipboard-write` in their
// Permissions-Policy — the shared execCommandCopyFallback (../clipboard)
// covers that path whenever the async API is missing or rejects.

async function copyToClipboard(args: HandlerArgs): Promise<HandlerResult> {
  const raw = (args.text as string | undefined) ?? "";
  // Normalise here rather than at each emitter: this is the single point where
  // text actually reaches the clipboard, so every Copy button funnels through
  // it — the deterministic one attached by submit_email_draft, the model's own
  // pill_office_clipboard_copy (still bound on email_draft, email_tone_adjuster,
  // send_attachment_draft / _compose and summarize_uploaded_file), and anything
  // added later. Draft bodies are HTML by construction, and writeText puts the
  // string on the clipboard verbatim, so without this the user pastes literal
  // "<p>Hi John,</p>" (AIVA-6393).
  const text = htmlToClipboardText(raw);
  if (!text) {
    throw new Error("clipboard.copy called with empty text");
  }
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      emitToast({ title: "Copied", intent: "success" });
      return { copied: true };
    } catch {
      // Permissions policy / NotAllowedError — fall through to execCommand.
    }
  }
  if (execCommandCopyFallback(text)) {
    emitToast({ title: "Copied", intent: "success" });
    return { copied: true };
  }
  emitToast({ title: "Couldn't copy. Try again.", intent: "error" });
  throw new Error("Clipboard API not available in this host");
}

interface OpenAppointmentOptions {
  handlerName: string;
  defaultLocation: string;
  successExtras: HandlerResult;
  failureMessage: string;
}

interface AppointmentDraft {
  subject: string;
  body: string;
  start: Date;
  end: Date;
  attendees: string[];
  location: string;
}

/** The lowercased domain of an email address, or "" when there isn't one. */
function domainOf(address: string): string {
  const at = (address ?? "").trim().toLowerCase().indexOf("@");
  return at === -1 ? "" : address.trim().toLowerCase().slice(at + 1);
}

/**
 * The attendee list with the organiser's own address removed.
 *
 * Specification §9, "Self-filtering": "The user's own address is removed from
 * attendee and recipient sets in both booking and drafting, at every resolution
 * step" — and CM-01 AC-7 names the Graph call as one of those steps. Inviting
 * yourself to your own event is at best noise on the invite and at worst an
 * extra mail; nothing upstream strips it on this path.
 */
export function withoutSelf(attendees: string[], organiserAddress: string): string[] {
  const self = (organiserAddress ?? "").trim().toLowerCase();
  if (!self) return attendees;
  return attendees.filter((a) => (a ?? "").trim().toLowerCase() !== self);
}

/**
 * Attendees whose domain differs from the organiser's, deduped, original casing.
 *
 * Mirrors the backend's ``meeting_validators.external_attendees`` so the two
 * gates cannot disagree about who is external: domain comparison is the whole
 * test — no directory call, no configured allow-list. Returns [] when the
 * organiser's own address is unknown, which is fail-open by design (§9 requires
 * graceful degradation; blocking every booking on an unclassifiable domain is a
 * worse outcome than not asking).
 */
export function externalAttendees(attendees: string[], organiserAddress: string): string[] {
  const organiserDomain = domainOf(organiserAddress);
  if (!organiserDomain) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const address of attendees) {
    const key = (address ?? "").trim().toLowerCase();
    const domain = domainOf(address);
    if (!domain || domain === organiserDomain || seen.has(key)) continue;
    seen.add(key);
    out.push(address.trim());
  }
  return out;
}

/**
 * Meetings this session has already booked through Graph, keyed on
 * subject + start + end + attendees and holding the created event so a repeat
 * click re-opens it instead of booking again. A Graph create is irreversible and
 * mails the invitations, so it must happen at most once per meeting; a
 * genuinely different meeting has a different key and is never blocked.
 *
 * Lives here rather than in ActionPills because the same descriptor also
 * reaches this handler from the model's own tool call, which no button guards.
 */
const bookedViaGraph = new Map<string, { id: string; webLink: string }>();

/** Test seam — the map is module state that would otherwise leak between cases. */
export function __resetGraphBookings(): void {
  bookedViaGraph.clear();
}

/**
 * CM-01 fallback for hosts with no native new-appointment form.
 *
 * Specification §6.2, verbatim: "A fallback for hosts without that API creates
 * the event via Graph after an external-recipient confirmation, then opens it
 * in the appointment form. Toast: '✅ Meeting booked'." AC-6: "Given the Graph
 * fallback path on a host without the native form, then external recipients are
 * confirmed before the event is created."
 *
 * The native form stays preferred wherever it exists — it is the user's chance
 * to verify inferred attendees before anything leaves the building. This path
 * only runs when there is no form to prefer.
 *
 * Declining the confirmation writes nothing at all: the POST is downstream of
 * the answer, so there is no partial event to clean up.
 */
async function bookViaGraphEvent(
  draft: AppointmentDraft,
  opts: OpenAppointmentOptions,
): Promise<HandlerResult> {
  // Attendees are part of the key: re-asking for the same slot with a
  // corrected attendee list is a different meeting and must not be swallowed
  // by the memo as "already booked".
  const attendeeKey = draft.attendees
    .map((a) => (a ?? "").trim().toLowerCase())
    .sort((a, b) => a.localeCompare(b))
    .join(",");
  const bookingKey = `${draft.subject}|${draft.start.toISOString()}|${draft.end.toISOString()}|${attendeeKey}`;
  const already = bookedViaGraph.get(bookingKey);
  if (already) {
    // Re-open rather than re-create. The Open pill re-enables once its click
    // settles (ActionPills only marks slot pills spent) and the model can
    // re-dispatch the same descriptor, so without this a second click would
    // create a second real event and mail a second invitation.
    const reopened = openExistingAppointment(already.id, already.webLink);
    return {
      opened: reopened,
      booked: true,
      already_booked: true,
      source: "graph_event",
      event_id: already.id,
      ...opts.successExtras,
    };
  }

  const { token, refresh } = getGraphAccess();
  if (!token) {
    // ST-03: with no MS365 connection, booking prompts the user to connect
    // rather than failing. A `message` renders as a chat line (see useChat's
    // reportOfficeActionResult) instead of the raw Office.js error this handler
    // used to throw.
    return {
      opened: false,
      message:
        "This version of Outlook can't open the meeting form, and I'm not connected to your Microsoft 365 " +
        "account, so I couldn't book it either. Connect Microsoft 365 in Settings and ask again.",
    };
  }

  // §9 self-filtering: "The user's own address is removed from attendee and
  // recipient sets in both booking and drafting, at every resolution step" —
  // AC-7 names this last step specifically. Done BEFORE the external
  // classification so the organiser can never be counted as an attendee at all.
  const organiser = Office.context?.mailbox?.userProfile?.emailAddress ?? "";
  const attendees = withoutSelf(draft.attendees, organiser);
  const outsiders = externalAttendees(attendees, organiser);
  if (outsiders.length > 0) {
    const confirmed = await requestConfirm({
      title: "Invite people outside your organisation?",
      message:
        `This meeting invites ${outsiders.join(", ")}, who are outside your organisation. ` +
        "Booking it will email them the invitation now.",
      confirmLabel: "Book and invite",
      cancelLabel: "Cancel",
    });
    if (!confirmed) {
      return {
        opened: false,
        cancelled: true,
        message: "I haven't booked the meeting. Tell me who you'd like to invite instead.",
      };
    }
  }

  // The Teams variant is NOT flagged `isOnlineMeeting` here: the backend has
  // already provisioned the meeting via /me/onlineMeetings and embedded its
  // join HTML in the body, so asking Graph to provision one would produce a
  // second, different join link on the same invite.
  const event = await createCalendarEvent(
    {
      subject: draft.subject,
      bodyHtml: draft.body,
      start: draft.start,
      end: draft.end,
      location: draft.location,
      attendees,
    },
    token,
    refresh,
  );

  bookedViaGraph.set(bookingKey, event);
  // §6.2 specifies "✅ Meeting booked"; G-13's approved closure removes emoji
  // from product toasts in favour of the design system's icon, which the
  // "success" intent already supplies. Every other toast in the add-in reads
  // this way, so the closure wins over the prototype's copy.
  emitToast({ title: "Meeting booked", intent: "success" });
  // `opened` reflects what actually happened: openExistingAppointment returns
  // false on its dead end (no display API and no webLink), and the booking
  // itself is already confirmed by the toast — claiming opened would be a lie.
  const openedNow = openExistingAppointment(event.id, event.webLink);
  return { opened: openedNow, booked: true, source: "graph_event", event_id: event.id, ...opts.successExtras };
}

async function openAppointmentForm(
  args: HandlerArgs,
  opts: OpenAppointmentOptions,
): Promise<HandlerResult> {
  const subject = (args.subject as string | undefined) ?? "";
  const body = (args.body as string | undefined) ?? "";
  const startStr = args.start as string | undefined;
  const endStr = args.end as string | undefined;
  const attendees = (args.attendees as string[] | undefined) ?? [];
  const location = (args.location as string | undefined) ?? opts.defaultLocation;

  if (!startStr || !endStr) {
    throw new TypeError(`${opts.handlerName} requires both start and end ISO datetime strings`);
  }

  const start = new Date(startStr);
  const end = new Date(endStr);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    throw new TypeError(`${opts.handlerName} got invalid start/end datetime — expected ISO 8601`);
  }

  const mailbox = Office.context?.mailbox;
  if (!mailbox?.displayNewAppointmentFormAsync) {
    // New Outlook for Windows, OWA and some Mac builds expose no native form.
    // Booking degrades to the Graph event path (§6.2) instead of throwing —
    // the raw Office.js error used to reach the user as a chat message.
    return bookViaGraphEvent({ subject, body, start, end, attendees, location }, opts);
  }

  return new Promise<HandlerResult>((resolve, reject) => {
    mailbox.displayNewAppointmentFormAsync(
      { requiredAttendees: attendees, subject, body, start, end, location },
      (res) => {
        if (res.status === Office.AsyncResultStatus.Succeeded) {
          resolve({ opened: true, source: "native_form", ...opts.successExtras });
        } else {
          reject(new Error(res.error?.message ?? opts.failureMessage));
        }
      },
    );
  });
}

async function openCalendar(args: HandlerArgs): Promise<HandlerResult> {
  return openAppointmentForm(args, {
    handlerName: "calendar.open",
    defaultLocation: "",
    successExtras: {},
    failureMessage: "Failed to open calendar form",
  });
}

// submit_teams_meeting_draft has already composed the body server-side —
// it embeds the real Teams join HTML (joinInformation) returned by
// Graph /me/onlineMeetings, or, if Graph couldn't provision a meeting,
// a manual-toggle banner. Either way the body comes in ready to ship,
// so this handler just opens the Outlook new-appointment form.
async function openTeamsCalendar(args: HandlerArgs): Promise<HandlerResult> {
  return openAppointmentForm(args, {
    handlerName: "calendar.open_teams",
    defaultLocation: "Microsoft Teams Meeting",
    successExtras: { online_meeting: true },
    failureMessage: "Failed to open Teams meeting form",
  });
}

async function renderEmailCards(args: HandlerArgs): Promise<HandlerResult> {
  const raw =
    (args.cards as Array<Record<string, unknown>> | undefined) ??
    (args.results as Array<Record<string, unknown>> | undefined) ??
    (args.emails as Array<Record<string, unknown>> | undefined);
  if (!Array.isArray(raw) || raw.length === 0) {
    return { rendered: false, count: 0 };
  }
  const cards: EmailSearchResult[] = raw.map((c) => ({
    id: _str(c.id),
    subject: _str(c.subject),
    from: _str(c.from) || _str(c.from_address),
    fromName: _str(c.from_name) || _str(c.fromName),
    date: _str(c.date),
    preview: _str(c.preview),
    hasAttachments: Boolean(c.has_attachments ?? c.hasAttachments ?? false),
    webLink: _str(c.web_link) || _str(c.webLink),
    conversationId: typeof c.conversation_id === "string" ? c.conversation_id : undefined,
  }));
  const querySummary = _str(args.query_summary) || _str(args.querySummary) || "Related emails";
  emitEmailCards(cards, querySummary);
  return { rendered: true, count: cards.length };
}

function _str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function _normaliseSubject(raw: string): string {
  return raw.replace(/^\s*(re|fw|fwd)\s*:\s*/i, "").trim().toLowerCase();
}

/**
 * Whether the open item is no longer in the conversation this reply button was
 * built for (see replyTarget.ts for why conversation, not message).
 *
 * False when either id is unknown — a pill from an older build or a rehydrated
 * turn carries no target, and a host without `conversationId` cannot be checked —
 * so those keep today's behaviour rather than refusing every click.
 */
function replyTargetMoved(args: HandlerArgs, item: { conversationId?: unknown } | null | undefined): boolean {
  const targetId = args[REPLY_TARGET_CONVERSATION_ARG];
  const openId = item?.conversationId;
  if (typeof targetId !== "string" || !targetId || typeof openId !== "string" || !openId) return false;
  // `MailboxItem.conversationId` was stored REST-converted; the live id is raw
  // (EWS-shaped on classic desktop), so convert before comparing.
  return toRestId(openId) !== targetId;
}

/**
 * Where a `compose.reply` click without a `draft_id` should land.
 *
 * Office.js's displayReplyForm only works on the currently-open mail item — it
 * cannot reply to an arbitrary email by id. So:
 *
 *  - `open_item` — the open email is in the conversation this reply is for.
 *  - `new_message` — no email is open, or the draft names a different thread.
 *    displayReplyForm would slam the body into the WRONG thread, so open a new
 *    compose pane with the draft's recipients and the reply lands where intended.
 *  - `stale` — the button was built for a conversation that is no longer open, and
 *    there is no draft subject or recipients to rebuild the reply from. The
 *    *Reply all* under an earlier `/reply` answer is the case (AIVA-8588): its
 *    turn's draft is a plain reply, so `draftArgs` drops the draft's envelope,
 *    and the subject test below had nothing to compare — the click wrote the
 *    earlier email's text into whatever was open. Refusing is the only safe
 *    answer; the user can reopen the email and click again.
 */
function replyTargetFor(
  item: { subject?: unknown; conversationId?: unknown } | null | undefined,
  draftSubject: string,
  args: HandlerArgs,
): "open_item" | "new_message" | "stale" {
  if (!item) return "new_message";
  const moved = replyTargetMoved(args, item);
  if (!draftSubject) return moved ? "stale" : "open_item";
  const openSubject = typeof item.subject === "string" ? item.subject : "";
  const subjectsMatch = !!openSubject && _normaliseSubject(draftSubject) === _normaliseSubject(openSubject);
  return subjectsMatch && !moved ? "open_item" : "new_message";
}

/** The `new_message` landing of {@link replyTargetFor}: a fresh compose form addressed from the draft. */
function openReplyAsNewMessage(draft: {
  to: string[];
  cc: string[];
  subject: string;
  body: string;
}): Promise<HandlerResult> {
  const mailbox = Office.context?.mailbox;
  if (!mailbox?.displayNewMessageFormAsync) {
    throw new Error("No open email matches this draft and a new compose form is unavailable");
  }
  return new Promise<HandlerResult>((resolve, reject) => {
    mailbox.displayNewMessageFormAsync(
      {
        toRecipients: draft.to,
        ccRecipients: draft.cc,
        subject: draft.subject,
        htmlBody: draft.body,
      },
      (res) => {
        if (res.status === Office.AsyncResultStatus.Succeeded) {
          resolve({
            opened: true,
            mode: "compose",
            fallback: "subject_mismatch",
            source: "native_form",
          });
        } else {
          reject(new Error(res.error?.message ?? "Failed to open compose form"));
        }
      },
    );
  });
}

async function composeReply(args: HandlerArgs): Promise<HandlerResult> {
  // ST-09. Every route out of this handler except the `draft_id` one below puts
  // this string into an outbound message — displayReplyForm, displayReplyAllForm
  // and the subject-mismatch compose fallback all take it as the body — so the
  // disclaimer is applied once, here, rather than three times downstream. A body
  // the backend already labelled is returned unchanged.
  const body = withOutlookDisclaimer((args.body as string | undefined) ?? "", "html");
  const mode = (args.mode as string | undefined) ?? "reply";
  // The LLM passes the draft's target subject/recipients alongside the body
  // when emitting a compose.reply pill; we use them as a fallback if the
  // currently-open email turns out to belong to a different thread.
  const draftSubject = (args.subject as string | undefined) ?? "";
  const draftTo = (args.to as string[] | undefined) ?? [];
  const draftCc = (args.cc as string[] | undefined) ?? [];

  // Threaded-reply path: when the backend pre-materialised a reply draft via
  // Graph createReply, it returns the new draft's id as `draft_id`. Opening
  // that draft via displayMessageForm gives the user a properly threaded
  // reply regardless of what's currently open in Outlook — In-Reply-To /
  // References / conversationId are all wired up server-side.
  const draftId = (args.draft_id as string | undefined) ?? "";
  if (draftId) {
    const mailbox = Office.context?.mailbox;
    if (mailbox?.displayMessageForm) {
      // Same REST->EWS conversion as openCompose. Without it the threaded reply
      // draft that the backend successfully created (draft_id present,
      // threaded: true) silently never opened, while the pill still toasted
      // "Reply ready — review and send." (AIVA-7482 testing, 2026-08-19).
      mailbox.displayMessageForm(toEwsId(draftId));
      return { opened: true, mode, threaded: true, source: "graph_create_reply" };
    }
    // Fall through to the Office.js path if displayMessageForm isn't available.
  }

  const item = Office.context?.mailbox?.item;
  const target = replyTargetFor(item, draftSubject, args);
  if (target === "stale") {
    // Matched by `isStaleTargetError`, which turns it into the "That action was
    // for a different email" toast — the same refusal the other stale paths give.
    throw new Error("No open email matches this reply — it was written for a different message");
  }
  if (!item || target === "new_message") {
    return openReplyAsNewMessage({ to: draftTo, cc: draftCc, subject: draftSubject, body });
  }

  if (mode === "reply_all") {
    if (typeof item.displayReplyAllForm !== "function") {
      throw new TypeError("displayReplyAllForm is not available in this host");
    }
    item.displayReplyAllForm(body);
  } else {
    if (typeof item.displayReplyForm !== "function") {
      throw new TypeError("displayReplyForm is not available in this host");
    }
    item.displayReplyForm(body);
  }
  return { opened: true, mode, source: "native_form" };
}

// Attachment-draft pill: open a Graph-created draft (with embedded attachment)
// via displayMessageForm. Separate from compose.open so the attachment path
// never falls through to displayNewMessageFormAsync, which would drop the
// attachment and is also unavailable in some Outlook surfaces.
async function pillOfficeOpenAttachmentDraft(args: HandlerArgs): Promise<HandlerResult> {
  const draftId = typeof args.draft_id === "string" ? args.draft_id : "";
  if (!draftId) {
    throw new Error("pill_office_open_attachment_draft requires draft_id");
  }
  // `kind` selects the button label only — both kinds open the pre-created
  // Graph draft the same way (displayMessageForm). "reply" is used by the
  // Reply-with-Attachment skill (the draft is a threaded createReply draft);
  // "open" (the default) is used by Compose-with-Attachment. The draft's
  // reply-vs-compose nature is decided server-side by which tool created it,
  // not by this label.
  const kind = args.kind === "reply" ? "reply" : "open";
  const label = kind === "reply" ? "Reply" : "Open";
  emitPills([
    {
      type: "office_action",
      label,
      handler: "attachment.open_draft",
      args: { draft_id: draftId },
    },
  ]);
  return { rendered: true };
}

async function openAttachmentDraft(args: HandlerArgs): Promise<HandlerResult> {
  const draftId = typeof args.draft_id === "string" ? args.draft_id : "";
  if (!draftId) {
    throw new Error("attachment.open_draft requires draft_id");
  }
  const mailbox = Office.context?.mailbox;
  if (!mailbox?.displayMessageForm) {
    throw new Error("displayMessageForm is not available in this host");
  }
  mailbox.displayMessageForm(draftId);
  return { opened: true, source: "attachment_draft" };
}

export const handlers: Record<string, HandlerFn> = {
  // Office.js handlers — invoked when the user clicks an office_action pill
  // OR when the LLM directly calls one of these (non-pill) frontend tools.
  "compose.open": openCompose,
  "compose.reply": composeReply,
  "mailbox.read_current": readCurrentMailbox,
  "compose.insert": insertAtCursor,
  "calendar.open": openCalendar,
  "calendar.open_teams": openTeamsCalendar,
  "clipboard.copy": copyToClipboard,
  // UI-only handlers — render cards or pill buttons without an Office.js call.
  "ui.render_email_cards": renderEmailCards,
  // Pill emission handlers — one per pill_* tool in ToolRegistry.
  "ui.pill.chat.refine_inbox": pillChatRefineInbox,
  "ui.pill.chat.refine_search": pillChatRefineSearch,
  "ui.pill.chat.tone_adjustment": pillChatToneAdjustment,
  "ui.pill.chat.book_slot": pillChatBookSlot,
  "ui.pill.chat.meeting_followup": pillChatMeetingFollowup,
  "ui.pill.chat.capability_launch": pillChatCapabilityLaunch,
  "ui.pill.chat.intent": pillChatIntent,
  "ui.pill.chat.prefill": pillChatNameCandidate,
  "ui.pill.office.compose_open": pillOfficeComposeOpen,
  "ui.pill.office.compose_reply": pillOfficeComposeReply,
  "ui.pill.office.calendar_open": pillOfficeCalendarOpen,
  "ui.pill.office.clipboard_copy": pillOfficeClipboardCopy,
  "ui.pill.office.open_attachment_draft": pillOfficeOpenAttachmentDraft,
  "attachment.open_draft": openAttachmentDraft,
};
